// src/contexts/NotificationContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
}

// âœ… Updated interface to include the helper functions
interface NotificationContextType {
  notifications: Notification[];
  addNotification: (type: NotificationType, message: string) => void;
  removeNotification: (id: string) => void;
  notifySuccess: (message: string) => void;  // â† Add this
  notifyError: (message: string) => void;    // â† Add this
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Custom hook to use the notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Add a new notification
  const addNotification = useCallback((type: NotificationType, message: string) => {
    const id = Date.now().toString();
    
    setNotifications(prev => [...prev, { id, type, message }]);
    
    // Auto-remove notifications after 5 seconds (except for errors)
    if (type !== 'error') {
      setTimeout(() => {
        removeNotification(id);
      }, 5000);
    }
  }, []);

  // Remove a notification by ID
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // âœ… Add these helper functions
  const notifySuccess = useCallback((message: string) => {
    addNotification('success', message);
  }, [addNotification]);

  const notifyError = useCallback((message: string) => {
    addNotification('error', message);
  }, [addNotification]);

  // âœ… Updated context value to include helper functions
  const value = {
    notifications,
    addNotification,
    removeNotification,
    notifySuccess,  // â† Add this
    notifyError     // â† Add this
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
